<template lang="pug">
div
  .mb-1
  .d-flex.justify-center.mt-16(v-if="isLoading")
    v-progress-circular(:width="2" :size="50" color="primary" indeterminate)

  div(v-else)
    v-row
      v-col(cols="12" sm="8")
        draggable(handle=".handle" draggable=".can-drag" :list="reasons" @start="drag = true" @end="endDrag")
          div(v-for="reason in reasons" :key="reason.id" :class="!reason.is_custom ? 'can-drag' : ''")
            v-card.round-xl.px-5.mb-3(outlined height="86")
              .d-flex.align-center.fill-height
                template(v-if="reason.is_custom")
                  v-icon(color="neutral300") $yzi-dots-grid-02
                template(v-else)
                  .handle.cursor-grab
                    v-icon(color="neutral500") $yzi-dots-grid-02
                .ml-4
                  .d-flex.align-center
                    .text-md.font-medium.neutral700--text {{ reason.name | capitalize }}
                    v-chip.text-sm.neutral500--text.ml-2.px-2(v-if="!reason.is_custom" label small color="neutral300")
                      v-icon(small) $yzi-list
                      .ml-1 List
                  .text-sm.neutral700--text.mt-1 Total respond: {{ reason.total_customers }}
                v-spacer
                div
                  y-switch.mb-n2(v-model="reason.enabled" @change="enableItem(reason)")
                v-menu(offset-y left rounded="lg")
                  template(#activator="{ on }")
                    v-btn.ml-4(icon small v-on="on")
                      v-icon mdi-dots-horizontal
                  v-list.py-0(dense width="120")
                    v-list-item(@click="editItem(reason)")
                      .text-sm.basic800--text.font-normal {{ $t('edit') }}
                    v-divider
                    v-list-item(@click="deleteItem(reason)")
                        .text-sm.danger500--text.font-normal {{ $t('delete') }}

        v-card.round-xl.px-4.border-dashed(link outlined height="86" @click="addRejectReason")
          .d-flex.align-center.fill-height
            v-avatar(color="neutral300" size="32")
              v-icon(color="neutral500" size="18") $yzi-plus
            .text-md.font-semibold.ml-4 Add reject reason
      
      v-col(cols="12" sm="4")
        .d-flex
          .pr-2
            .font-semibold.text-md Enable "Other" option
            .text-sm Allow to fill custom reason. This reason will be titled "Other"
          div
            y-switch(color="#333333" v-model="enableOther" @change="updateEnableOther")
        
  .dialogs
    add-reject-reason-dialog(v-if="showAddDialog" :open.sync="showAddDialog" :reject-status="rejectStatus" @added="reset")
    edit-reject-reason-dialog(v-if="showEditDialog" :open.sync="showEditDialog" :reason="selectedReason" :rejectStatus="rejectStatus" @updated="reset")
    delete-reject-reason-dialog(v-if="showDeleteDialog" :open.sync="showDeleteDialog" :reason="selectedReason" :rejectStatus="rejectStatus" @deleted="reset")

</template>

<script>
import draggable from 'vuedraggable'
import { mapGetters, mapActions } from 'vuex'
import AddRejectReasonDialog from '@/components/connect/dialogs/AddRejectReasonDialog.vue'
import EditRejectReasonDialog from '@/components/connect/dialogs/EditRejectReasonDialog.vue'
import DeleteRejectReasonDialog from '@/components/connect/dialogs/DeleteRejectReasonDialog.vue'

export default {
  name: 'RejectReasonList',
  components: { draggable, AddRejectReasonDialog, EditRejectReasonDialog, DeleteRejectReasonDialog },
  data: () => ({
    isLoading: false,
    reasons: [],
    rejectStatus: null,
    selectedReason: null,
    showAddDialog: false,
    showEditDialog: false,
    showDeleteDialog: false,
    drag: false,
    enableOther: false
  }),
  created () {
    this.fetchStatuses()
    this.fetchSettings()
  },
  computed: {
    ...mapGetters({ company: 'getCompany' }),
  },
  methods: {
    ...mapActions(['showMessage']),
    fetchStatuses () {
      this.isLoading = true
      this.$axios.get(`/connect/${this.company.slug}/statuses?exclude=customer`)
        .then(resp => {
          this.statuses = resp.data.results
          this.rejectStatus = this.statuses.find(item => item.name === 'reject')
          this.fetchRejectReasons()
        }) 
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    fetchRejectReasons () {
      this.$axios.get(`/connect/${this.company.slug}/statuses/${this.rejectStatus.id}/reasons`)
        .then(resp => {
          this.reasons = resp.data.results
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    fetchSettings () {
      this.$axios.get(`/connect/${this.company.slug}/settings`)
        .then(resp => {
          this.enableOther = resp.data?.extra_data?.pipeline_status?.allow_custom_reject_reasons
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    enableItem (reason) {
      this.$axios.patch(`/connect/${this.company.slug}/statuses/${this.rejectStatus.id}/reasons/${reason.id}`, { enabled: reason.enabled })
        .then(() => {
          this.showMessage(['success', this.$t('success_message.successfully_updated')])
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    editItem (reason) {
      this.selectedReason = reason
      this.showEditDialog = true
    },
    deleteItem (reason) {
      this.selectedReason = reason
      this.showDeleteDialog = true
    },
    reset () {
      this.selectedReason = null
      this.fetchRejectReasons()
    },
    endDrag (event) {
      this.drag = false
      const position = event.newIndex
      const reason = this.reasons[position]
      if (reason) {
        this.$axios.post(`/connect/${this.company.slug}/statuses/${this.rejectStatus.id}/reasons/${reason.id}/move`, { position })
          .then(() => {})
          .catch(() => {
            this.showMessage(['error', "Error moving reject reason"])
          })
      }
    },
    updateEnableOther () {
      const params = {
        extra_data: {
          pipeline_status: {
            allow_custom_reject_reasons: this.enableOther
          }
        }
      }
      this.$axios.post(`/connect/${this.company.slug}/settings`, params)
        .then(() => {
          this.showMessage(['success', this.$t('success_message.update_successful')])
        })
        .catch(() => {
          this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
        })
    },
    addRejectReason () {
      this.showAddDialog = true
    }
  }
}
</script>
